import { useEffect, useMemo, useRef, useState, Fragment } from "react";
import Layout, { PageHeader, Card } from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { formatINR, formatNumber, formatPercent, formatDate } from "../lib/format";
import { Upload, FileSpreadsheet, Trash2, Loader2, TrendingUp, AlertTriangle, ArrowUpRight, Activity } from "lucide-react";

const CATEGORIES = ["GA", "Fanpit", "Lounge", "VIP", "Other"];
const SCARCITY_THRESHOLD = 80; // % sold to trigger flag

function phaseStatus(p) {
  if (p.capacity > 0 && p.sold >= p.capacity) return "sold-out";
  if (p.sold > 0) return "live";
  return "future";
}

export default function Tickets() {
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState([]);
  const [velocity, setVelocity] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const reload = async () => {
    const [{ data: l }, { data: h }, { data: v }] = await Promise.all([
      api.get("/tickets/latest"),
      api.get("/tickets/history"),
      api.get("/tickets/velocity"),
    ]);
    setLatest(l && l.id ? l : null);
    setHistory(h);
    setVelocity(v && v.latest ? v : null);
  };
  useEffect(() => { reload(); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(""); setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/tickets/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await reload();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeUpload = async (id) => {
    if (!confirm("Delete this upload?")) return;
    await api.delete(`/tickets/${id}`);
    await reload();
  };

  const phases = latest?.phases || [];
  const byCategory = CATEGORIES.map((c) => {
    const rows = phases.filter((p) => p.category === c);
    return {
      category: c,
      sold: rows.reduce((s, r) => s + r.sold, 0),
      capacity: rows.reduce((s, r) => s + r.capacity, 0),
      revenue: rows.reduce((s, r) => s + r.sold * r.price, 0),
      rows: rows.length,
    };
  }).filter((c) => c.rows > 0);

  return (
    <Layout>
      <PageHeader
        overline="BookMyShow telemetry"
        title="Ticket sales"
        subtitle="Upload the BMS Category Wise Summary export to refresh sales numbers."
        actions={
          <>
            <input ref={inputRef} type="file" accept=".xlsx,.xlsm" onChange={onFile} className="hidden" data-testid="ticket-file-input" />
            <button
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              data-testid="upload-tickets-btn"
              className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black text-sm font-medium px-4 py-2 transition-colors"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {uploading ? "Parsing…" : "Upload .xlsx"}
            </button>
          </>
        }
        testId="tickets-header"
      />

      {error && (
        <div data-testid="upload-error" className="mb-6 border border-red-500/30 bg-red-500/10 text-red-200 text-sm px-4 py-3">
          {error}
        </div>
      )}

      {!latest && !uploading && (
        <Card className="p-12 border-dashed border-zinc-800 text-center">
          <FileSpreadsheet className="h-10 w-10 text-zinc-700 mx-auto mb-4" />
          <div className="font-display text-xl mb-1">No snapshot uploaded yet</div>
          <div className="text-sm text-zinc-500">Drop your BookMyShow Category Wise Summary xlsx to get started.</div>
        </Card>
      )}

      {latest && (
        <>
          {/* Top KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card className="p-5" testId="kpi-total-sold">
              <div className="overline mb-2">Total sold</div>
              <div className="font-display text-3xl font-bold tabular">{formatNumber(latest.total_sold)}</div>
              <div className="text-xs text-zinc-500 mt-1">of {formatNumber(latest.total_capacity)} capacity</div>
            </Card>
            <Card className="p-5" testId="kpi-fill-pct">
              <div className="overline mb-2">Fill %</div>
              <div className="font-display text-3xl font-bold tabular">
                {formatPercent(latest.total_sold, latest.total_capacity)}
              </div>
              <div className="mt-3 h-1 bg-zinc-900">
                <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, latest.total_capacity ? (latest.total_sold/latest.total_capacity)*100 : 0)}%` }} />
              </div>
            </Card>
            <Card className="p-5" testId="kpi-gross-revenue">
              <div className="overline mb-2">Gross revenue</div>
              <div className="font-display text-3xl font-bold tabular">{formatINR(latest.gross_revenue / 1e7)}</div>
              <div className="text-xs text-zinc-500 mt-1">From {phases.length} phases</div>
            </Card>
            <Card className="p-5" testId="kpi-uploaded">
              <div className="overline mb-2">Snapshot</div>
              <div className="font-display text-lg leading-tight">{formatDate(latest.uploaded_at)}</div>
              <div className="text-xs text-zinc-500 mt-1 truncate">{latest.filename}</div>
            </Card>
          </div>

          {/* Per-category */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {byCategory.map((c) => (
              <Card key={c.category} className="p-4" testId={`cat-${c.category}`}>
                <div className="overline mb-2">{c.category}</div>
                <div className="font-display text-2xl font-bold tabular">{formatNumber(c.sold)}</div>
                <div className="text-xs text-zinc-500 mt-1 tabular">
                  / {formatNumber(c.capacity)} · {formatPercent(c.sold, c.capacity)}
                </div>
                <div className="mt-2 h-[3px] bg-zinc-900">
                  <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, c.capacity ? (c.sold/c.capacity)*100 : 0)}%` }} />
                </div>
              </Card>
            ))}
          </div>

          {/* Sales velocity + Scarcity monitor */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
            <Card className="p-6" testId="velocity-card">
              <div className="flex items-end justify-between mb-5">
                <div>
                  <div className="overline mb-2 flex items-center gap-2"><TrendingUp className="h-3.5 w-3.5" /> Sales velocity</div>
                  <div className="font-display text-lg">Day-on-day units sold</div>
                </div>
                <div className="text-right text-xs text-zinc-500">
                  {velocity?.previous
                    ? `vs upload ${formatDate(velocity.previous.uploaded_at)} · ${velocity.hours_elapsed}h ago`
                    : "Upload another snapshot to compute velocity"}
                </div>
              </div>
              {velocity?.previous ? (
                <div className="space-y-3">
                  {byCategory.map((c) => {
                    const v = velocity.by_category?.[c.category];
                    if (!v) return null;
                    const positive = v.delta > 0;
                    return (
                      <div key={c.category} className="flex items-center gap-4" data-testid={`velocity-${c.category}`}>
                        <span className="w-20 text-sm text-zinc-300">{c.category}</span>
                        <div className="flex-1 flex items-baseline gap-2">
                          <span className={`font-display text-2xl font-bold tabular ${positive ? "text-emerald-400" : "text-zinc-500"}`}>
                            {positive ? "+" : ""}{formatNumber(v.delta)}
                          </span>
                          <span className="text-xs text-zinc-500">units</span>
                        </div>
                        <div className="text-right tabular text-sm">
                          <div className={`${positive ? "text-emerald-400" : "text-zinc-500"} flex items-center justify-end gap-1`}>
                            {positive && <ArrowUpRight className="h-3.5 w-3.5" />}
                            {v.units_per_day != null ? `${formatNumber(v.units_per_day)} /day` : "—"}
                          </div>
                          <div className="text-[10px] text-zinc-600 mt-0.5">over {v.hours_elapsed}h</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="py-8 text-center text-sm text-zinc-500">
                  Upload at least 2 BMS snapshots to see velocity between them.
                </div>
              )}
            </Card>

            <Card className="p-6" testId="scarcity-card">
              <div className="flex items-end justify-between mb-5">
                <div>
                  <div className="overline mb-2 flex items-center gap-2"><Activity className="h-3.5 w-3.5" /> Scarcity monitor</div>
                  <div className="font-display text-lg">% remaining per tier</div>
                </div>
                <div className="text-right text-xs text-zinc-500">Flag at {SCARCITY_THRESHOLD}%+ sold</div>
              </div>
              <div className="space-y-3">
                {byCategory.map((c) => {
                  const pct = c.capacity ? (c.sold / c.capacity) * 100 : 0;
                  const remainingPct = 100 - pct;
                  const flag = pct >= SCARCITY_THRESHOLD;
                  return (
                    <div key={c.category} data-testid={`scarcity-${c.category}`}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <div className="flex items-center gap-2">
                          <span className="text-zinc-300">{c.category}</span>
                          {flag && (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-amber-300 border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5">
                              <AlertTriangle className="h-3 w-3" /> Low stock
                            </span>
                          )}
                        </div>
                        <div className="tabular text-zinc-400">
                          <span className={flag ? "text-amber-300 font-medium" : "text-white"}>{remainingPct.toFixed(0)}%</span>
                          <span className="text-zinc-600"> remaining · {formatNumber(c.capacity - c.sold)} left</span>
                        </div>
                      </div>
                      <div className="h-1.5 bg-zinc-900 rounded-sm overflow-hidden">
                        <div className={`h-full ${flag ? "bg-amber-500" : "bg-emerald-500"} transition-all`} style={{ width: `${Math.min(100, pct)}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>

          {/* Phase table — grouped by category */}
          <Card className="overflow-hidden mb-8" testId="phase-table-card">
            <div className="px-6 py-4 border-b border-zinc-900 flex items-end justify-between">
              <div>
                <div className="overline mb-1">Phases · {phases.length} rows</div>
                <div className="font-display text-lg">Per-phase breakdown by category</div>
              </div>
              <div className="text-xs text-zinc-500 flex items-center gap-4">
                <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 bg-emerald-500" /> Sold out</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 bg-amber-500" /> Live</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 bg-zinc-600" /> Future</span>
              </div>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-[#181818] sticky top-0">
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                  <th className="px-4 py-3 font-medium">Phase</th>
                  <th className="px-4 py-3 font-medium text-right">Price</th>
                  <th className="px-4 py-3 font-medium text-right">Capacity</th>
                  <th className="px-4 py-3 font-medium text-right">Sold</th>
                  <th className="px-4 py-3 font-medium text-right">Available</th>
                  <th className="px-4 py-3 font-medium text-right">% Sold</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {byCategory.map((cat) => {
                  const catPhases = phases
                    .filter((p) => p.category === cat.category)
                    .sort((a, b) => {
                      const pa = a.capacity ? a.sold / a.capacity : 0;
                      const pb = b.capacity ? b.sold / b.capacity : 0;
                      return pb - pa;
                    });
                  if (catPhases.length === 0) return null;
                  return (
                    <Fragment key={cat.category}>
                      <tr className="bg-[#0f0f0f]">
                        <td colSpan={7} className="px-4 py-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="overline text-emerald-400">{cat.category}</span>
                              <span className="text-xs text-zinc-500">{catPhases.length} phases</span>
                            </div>
                            <span className="text-xs text-zinc-400 tabular">
                              {formatNumber(cat.sold)} / {formatNumber(cat.capacity)} ·
                              <span className="ml-1 text-emerald-400">{formatPercent(cat.sold, cat.capacity)}</span> ·
                              <span className="ml-1">{formatINR((cat.revenue || 0) / 1e7)}</span>
                            </span>
                          </div>
                        </td>
                      </tr>
                      {catPhases.map((p, i) => {
                        const st = phaseStatus(p);
                        const pct = p.capacity ? (p.sold / p.capacity) * 100 : 0;
                        const tint = st === "sold-out" ? "bg-emerald-500/5"
                                    : st === "live" ? "bg-amber-500/5"
                                    : "";
                        return (
                          <tr key={`${cat.category}-${i}`} className={`border-t border-zinc-900 ${tint} hover:bg-[#161616] transition-colors`} data-testid={`phase-row-${cat.category}-${i}`}>
                            <td className="px-4 py-2.5">{p.name}</td>
                            <td className="px-4 py-2.5 text-right tabular">₹ {formatNumber(p.price)}</td>
                            <td className="px-4 py-2.5 text-right tabular text-zinc-400">{formatNumber(p.capacity)}</td>
                            <td className="px-4 py-2.5 text-right tabular text-white">{formatNumber(p.sold)}</td>
                            <td className="px-4 py-2.5 text-right tabular text-zinc-400">{formatNumber(p.available)}</td>
                            <td className="px-4 py-2.5 text-right tabular">
                              <span className={pct >= SCARCITY_THRESHOLD ? "text-amber-300" : "text-zinc-300"}>
                                {pct.toFixed(0)}%
                              </span>
                            </td>
                            <td className="px-4 py-2.5">
                              <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.18em] border rounded-sm ${
                                st === "sold-out" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/20"
                                : st === "live" ? "bg-amber-500/15 text-amber-400 border-amber-500/20"
                                : "bg-zinc-500/15 text-zinc-400 border-zinc-500/20"
                              }`}>
                                {st === "sold-out" ? "Sold out" : st === "live" ? "Live" : "Future"}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </Card>
        </>
      )}

      {/* History */}
      <Card className="overflow-hidden" testId="upload-history-card">
        <div className="px-6 py-4 border-b border-zinc-900">
          <div className="overline mb-1">Upload history</div>
          <div className="font-display text-lg">{history.length} snapshots</div>
        </div>
        {history.length === 0 ? (
          <div className="px-6 py-10 text-center text-zinc-500 text-sm">No uploads yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#181818]">
              <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">File</th>
                <th className="px-4 py-3 font-medium">Uploaded by</th>
                <th className="px-4 py-3 font-medium text-right">Total sold</th>
                <th className="px-4 py-3 font-medium text-right">Gross</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id} className="border-t border-zinc-900 hover:bg-[#161616]" data-testid={`history-${h.id}`}>
                  <td className="px-4 py-2.5">{formatDate(h.uploaded_at)}</td>
                  <td className="px-4 py-2.5 text-zinc-300">{h.filename}</td>
                  <td className="px-4 py-2.5 text-zinc-400">{h.uploaded_by}</td>
                  <td className="px-4 py-2.5 text-right tabular">{formatNumber(h.total_sold)}</td>
                  <td className="px-4 py-2.5 text-right tabular text-emerald-400">{formatINR((h.gross_revenue || 0) / 1e7)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={() => removeUpload(h.id)} className="p-1.5 text-red-400 hover:bg-[#1a1a1a]" data-testid={`delete-upload-${h.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </Layout>
  );
}
